import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { dealsAPI, lendersAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import LenderPackagePreview from '@/components/LenderPackagePreview';
import { toast } from 'sonner';
import {
  ArrowLeft, User, Truck, FileText, MessageSquare, Upload, Download, Eye,
  Send, Sparkles, CheckCircle, Clock, Package, DollarSign, Calendar,
  Percent, CreditCard, Loader2, Building2, Mail, RefreshCw, PenTool, Link as LinkIcon, Files, Trash2,
  Pencil, Plus, Search
} from 'lucide-react';

const STATUSES = [
  { value: 'submitted', label: 'Submitted', color: 'bg-purple-100 text-purple-700 border-purple-300' },
  { value: 'in_review', label: 'In Review', color: 'bg-blue-100 text-blue-700 border-blue-300' },
  { value: 'approved', label: 'Approved', color: 'bg-green-100 text-green-700 border-green-300' },
  { value: 'declined', label: 'Declined', color: 'bg-red-100 text-red-700 border-red-300' },
  { value: 'on_hold', label: 'On Hold', color: 'bg-amber-100 text-amber-700 border-amber-300' },
  { value: 'callback', label: 'Callback', color: 'bg-cyan-100 text-cyan-700 border-cyan-300' },
  { value: 'funded', label: 'Funded', color: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
];

const getStatus = (stage) => STATUSES.find(s => s.value === stage) || { label: stage || 'Unknown', color: 'bg-gray-100 text-gray-600 border-gray-300' };

export default function ApplicationDetail() {
  const { dealId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const chatEndRef = useRef(null);
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [docs, setDocs] = useState([]);
  const [chatNotes, setChatNotes] = useState([]);
  const [chatMsg, setChatMsg] = useState('');
  const [sendingChat, setSendingChat] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [staffNotes, setStaffNotes] = useState('');
  const [brokerNotes, setBrokerNotes] = useState('');
  const [approvalOpen, setApprovalOpen] = useState(false);
  const [approvalForm, setApprovalForm] = useState({});
  const [savingApproval, setSavingApproval] = useState(false);
  const [sendLenderOpen, setSendLenderOpen] = useState(false);
  const [lenderPkgPreviewOpen, setLenderPkgPreviewOpen] = useState(false);
  const [lenderEmail, setLenderEmail] = useState('');
  const [sendingPackage, setSendingPackage] = useState(false);
  const [lenders, setLenders] = useState([]);
  const [sendMode, setSendMode] = useState('lender'); // 'lender' or 'custom'
  const [selectedLenderId, setSelectedLenderId] = useState('');
  // Lightbox state
  const [lightbox, setLightbox] = useState({ open: false, url: '', filename: '', type: '' });
  const [activityLog, setActivityLog] = useState([]);
  // Equipment management state
  const [editUnitOpen, setEditUnitOpen] = useState(false);
  const [editUnitData, setEditUnitData] = useState(null);
  const [editUnitMode, setEditUnitMode] = useState('edit'); // 'edit' | 'swap'
  const [swapSearch, setSwapSearch] = useState('');
  const [priceOverride, setPriceOverride] = useState('');
  const [equipOpen, setEquipOpen] = useState(false);
  const [inventory, setInventory] = useState([]);
  const [loadingInv, setLoadingInv] = useState(false);
  const [invSearch, setInvSearch] = useState('');
  const [savingEquip, setSavingEquip] = useState(false);
  const [customEquipOpen, setCustomEquipOpen] = useState(false);
  const [customEquip, setCustomEquip] = useState({ year: '', make: '', model: '', price: '', notes: '' });
  const [viewAllOpen, setViewAllOpen] = useState(false);
  // Document category upload state
  const [docUploadOpen, setDocUploadOpen] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [editingCatId, setEditingCatId] = useState(null);
  const DOC_CATEGORIES = ['Bank Statements', 'Credit App', 'Articles', 'Driving Licence', 'Financials', 'T2', 'Ownership Copy', 'POA', 'Bill of Sale', 'Invoice', 'Safety Certificate', 'Hold Deposit', 'Other'];

  const load = useCallback(async () => {
    try {
      const dealRes = await dealsAPI.get(dealId);
      setDeal(dealRes.data);
      setBrokerNotes(dealRes.data?.broker_notes || '');
    } catch { toast.error('Failed to load application'); }
    try { const docsRes = await dealsAPI.listDocs(dealId); setDocs(docsRes.data || []); } catch (e) { console.error('Docs load error:', e); }
    try { const chatRes = await dealsAPI.getChatNotes(dealId); setChatNotes(chatRes.data || []); } catch (e) { console.error('Chat load error:', e); }
    try { const alRes = await dealsAPI.getActivity(dealId); setActivityLog(alRes.data || []); } catch (e) { console.error('Activity load error:', e); }
    setLoading(false);
  }, [dealId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatNotes]);

  // Fetch lenders when send dialog opens
  useEffect(() => {
    if (sendLenderOpen) {
      lendersAPI.list().then(r => {
        const list = Array.isArray(r.data) ? r.data : r.data?.items || [];
        setLenders(list);
      }).catch(() => {});
    }
  }, [sendLenderOpen]);

  const handleStatusChange = async (newStatus) => {
    try {
      await dealsAPI.update(dealId, { stage: newStatus });
      setDeal(prev => ({ ...prev, stage: newStatus }));
      toast.success('Status updated');
    } catch { toast.error('Failed to update status'); }
  };

  // Equipment management handlers
  const { inventoryAPI } = require('@/lib/api');
  const openEquipPicker = async () => {
    setEquipOpen(true);
    setInvSearch('');
    setLoadingInv(true);
    try {
      const res = await inventoryAPI.list({ limit: 200 });
      setInventory((res.data?.items || res.data || []).filter(i => i.id));
    } catch { toast.error('Failed to load inventory'); }
    setLoadingInv(false);
  };

  const handleRemoveUnit = async (unitId) => {
    const currentIds = deal?.inventory_ids || [];
    const newIds = currentIds.filter(id => id !== unitId);
    setSavingEquip(true);
    try {
      await dealsAPI.update(dealId, { inventory_ids: newIds, inventory_id: newIds[0] || null });
      toast.success('Unit removed');
      load();
    } catch { toast.error('Failed to remove unit'); }
    setSavingEquip(false);
  };

  const handleAddUnit = async (unitId) => {
    const currentIds = deal?.inventory_ids || [];
    if (currentIds.includes(unitId)) { toast.info('Already in deal'); return; }
    const newIds = [...currentIds, unitId];
    setSavingEquip(true);
    try {
      await dealsAPI.update(dealId, { inventory_ids: newIds, inventory_id: newIds[0] });
      toast.success('Unit added');
      setEquipOpen(false);
      load();
    } catch { toast.error('Failed to add unit'); }
    setSavingEquip(false);
  };

  const handleSaveEditPrice = async (unitId) => {
    setSavingEquip(true);
    try {
      const overrides = { ...(deal?.price_overrides || {}) };
      if (priceOverride && parseFloat(priceOverride) > 0) {
        overrides[unitId] = parseFloat(priceOverride);
      } else {
        delete overrides[unitId];
      }
      await dealsAPI.update(dealId, { price_overrides: overrides });
      toast.success('Deal price updated');
      setEditUnitOpen(false);
      load();
    } catch { toast.error('Failed to update price'); }
    setSavingEquip(false);
  };

  const handleSwapUnit = async (oldUnitId, newUnit) => {
    const currentIds = deal?.inventory_ids || [];
    const newIds = currentIds.map(id => id === oldUnitId ? newUnit.id : id);
    setSavingEquip(true);
    try {
      const payload = { inventory_ids: newIds, inventory_id: newIds[0] };
      if (priceOverride) {
        payload.price_overrides = { ...(deal?.price_overrides || {}), [newUnit.id]: parseFloat(priceOverride) };
      }
      await dealsAPI.update(dealId, payload);
      toast.success(`Swapped to ${newUnit.year} ${newUnit.make} ${newUnit.model}`);
      setEditUnitOpen(false);
      load();
    } catch { toast.error('Failed to swap unit'); }
    setSavingEquip(false);
  };

  const handleSaveCustomEquip = async () => {
    setSavingEquip(true);
    try {
      await dealsAPI.update(dealId, { custom_equipment: customEquip, inventory_id: null, inventory_ids: [] });
      toast.success('Custom equipment saved');
      setCustomEquipOpen(false);
      setEquipOpen(false);
      load();
    } catch { toast.error('Failed to save'); }
    setSavingEquip(false);
  };

  const filteredInv = inventory.filter(u => {
    const currentIds = deal?.inventory_ids || [];
    if (currentIds.includes(u.id)) return false;
    if (!invSearch) return true;
    const s = invSearch.toLowerCase();
    return `${u.year} ${u.make} ${u.model} ${u.stock_number}`.toLowerCase().includes(s);
  });

  const filteredSwapInv = inventory.filter(u => {
    const currentIds = deal?.inventory_ids || [];
    if (currentIds.includes(u.id)) return false;
    if (!swapSearch) return true;
    const s = swapSearch.toLowerCase();
    return `${u.year} ${u.make} ${u.model} ${u.stock_number}`.toLowerCase().includes(s);
  });

  const handleGenerateBrokerNotes = async () => {
    setAiLoading(true);
    try {
      const clientInfo = deal?.client
        ? `${deal.client.name || ''}, ${deal.client.company || ''}, Email: ${deal.client.email || ''}, Phone: ${deal.client.phone || ''}`
        : '';
      const equipmentInfo = deal?.unit
        ? `${deal.unit.year || ''} ${deal.unit.make || ''} ${deal.unit.model || ''}, Stock #${deal.unit.stock_number || ''}, ${deal.unit.mileage_km ? deal.unit.mileage_km + ' km' : ''}, ${deal.unit.price ? '$' + Number(deal.unit.price).toLocaleString() : ''}`
        : '';
      const res = await dealsAPI.generateBrokerNotes({
        client_info: clientInfo,
        equipment_info: equipmentInfo,
        staff_notes: staffNotes || brokerNotes || 'Generate professional broker notes.',
      });
      const newNotes = res.data?.broker_notes || '';
      setBrokerNotes(newNotes);
      await dealsAPI.update(dealId, { broker_notes: newNotes });
      toast.success('Broker notes generated & saved');
    } catch { toast.error('AI generation failed'); }
    setAiLoading(false);
  };

  const handleSaveBrokerNotes = async () => {
    try {
      await dealsAPI.update(dealId, { broker_notes: brokerNotes });
      toast.success('Broker notes saved');
    } catch { toast.error('Failed to save'); }
  };

  const handleSendChat = async () => {
    if (!chatMsg.trim()) return;
    setSendingChat(true);
    try {
      const res = await dealsAPI.addChatNote(dealId, { content: chatMsg.trim() });
      setChatNotes(prev => [...prev, res.data]);
      setChatMsg('');
    } catch { toast.error('Failed to send message'); }
    setSendingChat(false);
  };

  const handleUploadDoc = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setPendingFiles(files.map(f => ({ file: f, category: '', customCategory: '' })));
    setDocUploadOpen(true);
    e.target.value = '';
  };

  const handleConfirmUpload = async () => {
    const uncategorized = pendingFiles.some(f => !f.category);
    if (uncategorized) { toast.error('Please select a category for all files'); return; }
    setUploading(true);
    setDocUploadOpen(false);
    let success = 0;
    for (const entry of pendingFiles) {
      try {
        const cat = entry.category === 'Other' ? (entry.customCategory || 'Other') : entry.category;
        await dealsAPI.uploadDoc(dealId, entry.file, cat);
        success++;
      } catch { /* continue */ }
    }
    if (success > 0) {
      toast.success(`${success} file(s) uploaded`);
      const res = await dealsAPI.listDocs(dealId);
      setDocs(res.data || []);
    }
    if (success < pendingFiles.length) toast.error(`${pendingFiles.length - success} file(s) failed`);
    setUploading(false);
    setPendingFiles([]);
  };

  const handleUpdateDocCategory = async (docId, newCat) => {
    try {
      await dealsAPI.updateDocCategory(dealId, docId, newCat);
      toast.success('Category updated');
      setEditingCatId(null);
      const res = await dealsAPI.listDocs(dealId);
      setDocs(res.data || []);
    } catch { toast.error('Failed to update category'); }
  };

  const handleDeleteDoc = async (docId) => {
    if (!confirm('Delete this document?')) return;
    try {
      await dealsAPI.deleteDealDoc(dealId, docId);
      toast.success('Document deleted');
      const res = await dealsAPI.listDocs(dealId);
      setDocs(res.data || []);
    } catch { toast.error('Failed to delete'); }
  };

  const handleDownloadZip = async () => {
    try {
      const res = await dealsAPI.downloadZip(dealId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `CTS_Credit_Package_${deal?.client?.name?.replace(/\s/g, '_') || 'deal'}.zip`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const viewDoc = async (doc) => {
    // Generated documents (BOS, quotes, invoices) have a url field
    if (doc.source === 'generated' && doc.url) {
      const ext = doc.filename?.split('.').pop()?.toLowerCase() || '';
      const isPdf = ext === 'pdf';
      const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
      window.open(`${API_URL}${doc.url}`, '_blank');
      return;
    }
    try {
      const res = await dealsAPI.downloadDoc(dealId, doc.id);
      const blob = new Blob([res.data], { type: doc.content_type || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const ext = doc.filename?.split('.').pop()?.toLowerCase() || '';
      const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext) || (doc.content_type && doc.content_type.startsWith('image/'));
      const isPdf = ext === 'pdf' || doc.content_type === 'application/pdf';
      setLightbox({ open: true, url, filename: doc.filename, type: isImage ? 'image' : isPdf ? 'pdf' : 'other' });
    } catch { toast.error('Failed to load document'); }
  };

  const handleDownloadDoc = async (doc) => {
    // Generated documents have a url field
    if (doc.source === 'generated' && doc.url) {
      const a = document.createElement('a');
      a.href = `${API_URL}${doc.url}`;
      a.download = doc.filename;
      a.click();
      return;
    }
    try {
      const res = await dealsAPI.downloadDoc(dealId, doc.id);
      const blob = new Blob([res.data], { type: doc.content_type || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const openApprovalForm = () => {
    setApprovalForm({
      financing_amount: deal?.financing_amount?.toString() || '',
      monthly_payment: deal?.monthly_payment?.toString() || '',
      term: deal?.term?.toString() || '',
      interest_rate: deal?.interest_rate?.toString() || '',
      downpayment: deal?.downpayment?.toString() || '',
      funding_date: deal?.funding_date || '',
    });
    setApprovalOpen(true);
  };

  const handleSaveApproval = async (e) => {
    e.preventDefault();
    setSavingApproval(true);
    try {
      const payload = { stage: 'approved' };
      ['financing_amount', 'monthly_payment', 'downpayment', 'interest_rate'].forEach(k => {
        payload[k] = approvalForm[k] ? parseFloat(approvalForm[k]) : null;
      });
      payload.term = approvalForm.term ? parseInt(approvalForm.term) : null;
      payload.funding_date = approvalForm.funding_date || null;
      await dealsAPI.update(dealId, payload);
      toast.success('Application approved with terms');
      setApprovalOpen(false);
      load();
    } catch { toast.error('Failed to save'); }
    setSavingApproval(false);
  };

  const handleSendToLender = async () => {
    let email = '';
    if (sendMode === 'lender') {
      const selected = lenders.find(l => l.id === selectedLenderId);
      email = selected?.contact_email || '';
      if (!email) { toast.error('Selected lender has no email address'); return; }
    } else {
      email = lenderEmail.trim();
      if (!email) { toast.error('Enter an email address'); return; }
    }
    setSendingPackage(true);
    try {
      await dealsAPI.sendPackage(dealId, { to_email: email });
      const lenderName = sendMode === 'lender' ? lenders.find(l => l.id === selectedLenderId)?.name : email;
      toast.success(`Credit package sent to ${lenderName}`);
      setSendLenderOpen(false);
      setLenderEmail('');
      setSelectedLenderId('');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to send package';
      toast.error(msg);
    }
    setSendingPackage(false);
  };

  if (loading) return <div className="animate-pulse space-y-4 max-w-6xl mx-auto">{[1,2,3].map(i => <div key={i} className="h-32 bg-gray-100 rounded-lg" />)}</div>;
  if (!deal) return <div className="text-center py-20 text-gray-400">Application not found</div>;

  const status = getStatus(deal.stage);
  const isApproved = ['approved', 'funded'].includes(deal.stage);

  return (
    <div data-testid="application-detail-page" className="max-w-6xl mx-auto">
      {/* Back + Header */}
      <div className="flex items-center gap-2 mb-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/deals')} className="text-gray-500" data-testid="back-to-applications">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to {isAdmin ? 'Applications' : 'Track Applications'}
        </Button>
      </div>

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900" data-testid="app-title">{deal.title || 'Credit Application'}</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Submitted {new Date(deal.created_at).toLocaleDateString()} by {deal.created_by || 'Unknown'}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin ? (
            <Select value={deal.stage} onValueChange={handleStatusChange}>
              <SelectTrigger className="w-[150px] h-8 text-xs border-0 shadow-none" data-testid="change-app-status">
                <Badge variant="outline" className={`text-xs px-3 py-1 ${status.color}`}>{status.label}</Badge>
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : (
            <Badge variant="outline" className={`text-xs px-3 py-1 ${status.color}`} data-testid="app-status-badge">{status.label}</Badge>
          )}
          {isAdmin && (
            <>
              <Button size="sm" variant="outline" className="border-green-300 text-green-700 text-xs h-8" onClick={openApprovalForm} data-testid="edit-approval-btn">
                <CheckCircle className="w-3 h-3 mr-1" /> {isApproved ? 'Edit Terms' : 'Approve'}
              </Button>
              <Button size="sm" variant="outline" className="border-[#0E7F8E] text-[#0E7F8E] hover:bg-[#0E7F8E]/5 text-xs h-8" onClick={() => setLenderPkgPreviewOpen(true)} data-testid="preview-lender-pkg-btn">
                <Eye className="w-3 h-3 mr-1" /> Preview Package
              </Button>
              <Button size="sm" className="bg-[#E31937] hover:bg-[#C4122C] text-white text-xs h-8" onClick={() => setSendLenderOpen(true)} data-testid="send-to-lender-btn">
                <Send className="w-3 h-3 mr-1" /> Send to Lender
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left column */}
        <div className="lg:col-span-2 space-y-5">
          {/* Client + Equipment row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 items-start">
            {/* Client Details */}
            <Card className="border-gray-200" data-testid="client-details-card">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <User className="w-4 h-4 text-[#E31937]" />
                  <h3 className="text-sm font-semibold text-gray-900 uppercase">Client Details</h3>
                </div>
                {deal.client ? (
                  <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between"><span className="text-gray-500">Name</span><span className="font-medium text-gray-900">{deal.client.name}</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">Company</span><span className="font-medium text-gray-900">{deal.client.company || '—'}</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">Email</span><span className="font-medium text-gray-900 text-xs">{deal.client.email || '—'}</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">Phone</span><span className="font-medium text-gray-900">{deal.client.phone || '—'}</span></div>
                  </div>
                ) : <p className="text-sm text-gray-400 italic">No client info</p>}
              </CardContent>
            </Card>

            {/* Equipment Details */}
            <Card className="border-gray-200" data-testid="equipment-details-card">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Truck className="w-4 h-4 text-[#E31937]" />
                  <h3 className="text-sm font-semibold text-gray-900 uppercase">Equipment Details</h3>
                </div>
                {deal.units && deal.units.length > 0 ? (
                  <div className="space-y-3">
                    {deal.units.slice(0, 1).map((u, idx) => (
                      <div key={u.id || idx} className={`space-y-1.5 text-sm ${idx > 0 ? 'pt-2 border-t border-gray-100' : ''}`}>
                        {deal.units.length > 1 && <p className="text-[10px] text-gray-400 uppercase font-medium">Unit {idx + 1}</p>}
                        <div className="flex justify-between"><span className="text-gray-500">Vehicle</span><span className="font-medium text-gray-900 text-right max-w-[60%] truncate">{u.year} {u.make} {u.model}</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">Stock #</span><span className="font-medium text-gray-900">{u.stock_number || '—'}</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">Mileage</span><span className="font-medium text-gray-900">{u.mileage_km ? `${Number(u.mileage_km).toLocaleString()} km` : '—'}</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">Price</span><span className="font-medium text-[#E31937]">{u.price ? `$${Number(u.price).toLocaleString()}` : '—'}</span></div>
                        <div className="flex gap-1.5 pt-1">
                          <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 border-gray-300 text-gray-600 hover:text-[#E31937] hover:border-[#E31937]/30"
                            onClick={() => { setEditUnitData(u); setEditUnitMode('edit'); setPriceOverride(deal?.price_overrides?.[u.id]?.toString() || ''); setEditUnitOpen(true); }}
                            data-testid={`edit-unit-${u.id || idx}`}
                          ><Pencil className="w-2.5 h-2.5 mr-1" />Edit</Button>
                          <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 border-gray-300 text-gray-600 hover:text-blue-600 hover:border-blue-300"
                            onClick={() => { setEditUnitData(u); setEditUnitMode('swap'); setSwapSearch(''); setPriceOverride(''); openEquipPicker(); setEditUnitOpen(true); }}
                            data-testid={`swap-unit-${u.id || idx}`}
                          ><RefreshCw className="w-2.5 h-2.5 mr-1" />Swap</Button>
                          {deal.units.length > 1 && (
                            <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 border-gray-300 text-red-400 hover:text-red-600 hover:border-red-300"
                              onClick={() => handleRemoveUnit(u.id)} disabled={savingEquip}
                              data-testid={`delete-unit-${u.id || idx}`}
                            ><Trash2 className="w-2.5 h-2.5 mr-1" />Delete</Button>
                          )}
                        </div>
                      </div>
                    ))}
                    {deal.units.length > 1 && (
                      <Button size="sm" variant="outline" className="w-full text-xs border-[#E31937]/30 text-[#E31937] hover:bg-[#E31937]/5"
                        onClick={() => setViewAllOpen(true)} data-testid="view-more-units-btn"
                      ><Eye className="w-3 h-3 mr-1" /> View all {deal.units.length} units</Button>
                    )}
                    <Button size="sm" variant="outline" className="w-full text-xs border-dashed border-gray-300 text-gray-500 hover:text-[#E31937] hover:border-[#E31937]/30"
                      onClick={openEquipPicker} data-testid="add-unit-to-deal-btn"
                    ><Plus className="w-3 h-3 mr-1" /> Add Unit</Button>
                  </div>
                ) : deal.unit ? (
                  <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between"><span className="text-gray-500">Vehicle</span><span className="font-medium text-gray-900 text-right max-w-[60%] truncate">{deal.unit.year} {deal.unit.make} {deal.unit.model}</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">Stock #</span><span className="font-medium text-gray-900">{deal.unit.stock_number || '—'}</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">Mileage</span><span className="font-medium text-gray-900">{deal.unit.mileage_km ? `${Number(deal.unit.mileage_km).toLocaleString()} km` : '—'}</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">Price</span><span className="font-medium text-[#E31937]">{deal.unit.price ? `$${Number(deal.unit.price).toLocaleString()}` : '—'}</span></div>
                    <div className="flex gap-1.5 pt-1">
                      <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 border-gray-300 text-gray-600 hover:text-[#E31937]"
                        onClick={() => { setEditUnitData(deal.unit); setEditUnitMode('edit'); setPriceOverride(deal?.price_overrides?.[deal.unit.id]?.toString() || ''); setEditUnitOpen(true); }}
                        data-testid="edit-single-unit"
                      ><Pencil className="w-2.5 h-2.5 mr-1" />Edit</Button>
                      <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 border-gray-300 text-gray-600 hover:text-blue-600"
                        onClick={() => { setEditUnitData(deal.unit); setEditUnitMode('swap'); setSwapSearch(''); setPriceOverride(''); openEquipPicker(); setEditUnitOpen(true); }}
                        data-testid="swap-single-unit"
                      ><RefreshCw className="w-2.5 h-2.5 mr-1" />Swap</Button>
                    </div>
                  </div>
                ) : deal.custom_equipment ? (
                  <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between"><span className="text-gray-500">Vehicle</span><span className="font-medium text-gray-900">{deal.custom_equipment.year} {deal.custom_equipment.make} {deal.custom_equipment.model}</span></div>
                    {deal.custom_equipment.price && <div className="flex justify-between"><span className="text-gray-500">Price</span><span className="font-medium text-[#E31937]">${Number(deal.custom_equipment.price).toLocaleString()}</span></div>}
                    <Badge variant="outline" className="text-[9px] border-blue-300 text-blue-600">Private Sale / Custom</Badge>
                    {deal.custom_equipment.notes && <p className="text-xs text-gray-500 mt-1">{deal.custom_equipment.notes}</p>}
                    <Button size="sm" variant="outline" className="w-full text-xs border-dashed border-gray-300 text-gray-500 hover:text-[#E31937] mt-2"
                      onClick={openEquipPicker} data-testid="change-equip-btn"
                    ><RefreshCw className="w-3 h-3 mr-1" /> Change Equipment</Button>
                  </div>
                ) : (
                  <div>
                    <p className="text-gray-400 italic text-sm mb-2">No equipment linked</p>
                    <Button size="sm" variant="outline" className="w-full text-xs border-dashed border-gray-300 text-gray-500 hover:text-[#E31937]"
                      onClick={openEquipPicker} data-testid="add-equip-btn"
                    ><Plus className="w-3 h-3 mr-1" /> Add Equipment</Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Approval Details (if approved) */}
          {isApproved && (
            <Card className="border-green-200 bg-green-50/50" data-testid="approval-details-card">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <h3 className="text-sm font-semibold text-green-800 uppercase">Approval Details</h3>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {[
                    { label: 'Financing Amount', val: deal.financing_amount, fmt: v => `$${Number(v).toLocaleString()}`, icon: DollarSign },
                    { label: 'Monthly Payment', val: deal.monthly_payment, fmt: v => `$${Number(v).toLocaleString()}`, icon: CreditCard },
                    { label: 'Term', val: deal.term, fmt: v => `${v} months`, icon: Clock },
                    { label: 'Interest Rate', val: deal.interest_rate, fmt: v => `${v}%`, icon: Percent },
                    { label: 'Down Payment', val: deal.downpayment, fmt: v => `$${Number(v).toLocaleString()}`, icon: DollarSign },
                    { label: 'Funding Date', val: deal.funding_date, fmt: v => new Date(v).toLocaleDateString(), icon: Calendar },
                  ].map(f => {
                    const Icon = f.icon;
                    return (
                      <div key={f.label} className="bg-white rounded-lg p-3 border border-green-100">
                        <div className="flex items-center gap-1 mb-1">
                          <Icon className="w-3 h-3 text-green-600" />
                          <p className="text-[10px] text-gray-500 uppercase">{f.label}</p>
                        </div>
                        <p className="text-sm font-semibold text-gray-900">{f.val ? f.fmt(f.val) : '—'}</p>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Broker Notes */}
          <Card className="border-gray-200" data-testid="broker-notes-card">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-[#E31937]" />
                  <h3 className="text-sm font-semibold text-gray-900 uppercase">Broker Notes</h3>
                </div>
                <div className="flex gap-2">
                  {brokerNotes && (
                    <Button size="sm" variant="outline" className="text-xs h-7 border-gray-300" onClick={handleSaveBrokerNotes} data-testid="save-broker-notes-btn">
                      Save
                    </Button>
                  )}
                  <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white text-xs h-7" onClick={handleGenerateBrokerNotes} disabled={aiLoading} data-testid="ai-generate-btn">
                    {aiLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                    {aiLoading ? 'Generating...' : 'AI Generate'}
                  </Button>
                </div>
              </div>
              <Input placeholder="Optional: Add your own notes for AI context..." value={staffNotes} onChange={e => setStaffNotes(e.target.value)} className="bg-gray-50 border-gray-200 text-xs mb-2 h-8" data-testid="staff-notes-input" />
              <Textarea
                placeholder="Broker notes will appear here after AI generation, or type manually..."
                value={brokerNotes}
                onChange={e => setBrokerNotes(e.target.value)}
                className="bg-gray-50 border-gray-200 text-sm min-h-[120px]"
                data-testid="broker-notes-textarea"
              />
            </CardContent>
          </Card>

          {/* Documents */}
          <Card className="border-gray-200" data-testid="documents-card">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Package className="w-4 h-4 text-[#E31937]" />
                  <h3 className="text-sm font-semibold text-gray-900 uppercase">Documents ({docs.length})</h3>
                </div>
                <div className="flex gap-2">
                  {docs.length > 0 && isAdmin && (
                    <Button size="sm" variant="outline" className="text-xs h-7 border-gray-300" onClick={handleDownloadZip} data-testid="download-zip-btn">
                      <Download className="w-3 h-3 mr-1" /> Download All
                    </Button>
                  )}
                  <Button size="sm" className="bg-gray-900 hover:bg-gray-800 text-white text-xs h-7" onClick={() => document.getElementById('app-file-upload').click()} data-testid="upload-doc-btn">
                      {uploading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}
                      {uploading ? 'Uploading...' : 'Upload'}
                    </Button>
                    <input id="app-file-upload" type="file" className="hidden" multiple onChange={handleUploadDoc} disabled={uploading} data-testid="file-upload-input" />
                </div>
              </div>
              {docs.length === 0 ? (
                <p className="text-sm text-gray-400 italic text-center py-6">No documents uploaded yet</p>
              ) : (
                <div className="space-y-1.5">
                  {docs.map(doc => (
                    <div key={doc.id} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded text-sm" data-testid={`doc-row-${doc.id}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="w-4 h-4 text-[#E31937] flex-shrink-0" />
                        <span className="text-gray-800 truncate">{doc.display_name || doc.original_name || doc.filename}</span>
                        {doc.source !== 'generated' && editingCatId === doc.id ? (
                          <Select value={doc.doc_type || ''} onValueChange={v => handleUpdateDocCategory(doc.id, v)}>
                            <SelectTrigger className="h-6 text-[10px] w-auto min-w-[120px] bg-white border-gray-300" data-testid={`edit-cat-select-${doc.id}`}>
                              <SelectValue placeholder="Category..." />
                            </SelectTrigger>
                            <SelectContent>
                              {DOC_CATEGORIES.map(cat => (
                                <SelectItem key={cat} value={cat} className="text-xs">{cat}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Badge variant="outline" className={`text-[9px] ${doc.source === 'client' ? 'border-blue-300 text-blue-600' : doc.source === 'generated' ? 'border-green-300 text-green-600' : 'border-gray-300 text-gray-500'} ${doc.source !== 'generated' ? 'cursor-pointer hover:opacity-80' : ''}`}
                            onClick={() => doc.source !== 'generated' && setEditingCatId(doc.id)} data-testid={`edit-cat-badge-${doc.id}`}>
                            {doc.source === 'client' ? (doc.doc_type || 'Client Upload') : doc.source === 'generated' ? doc.doc_type?.replace(/_/g, ' ') : doc.doc_type}
                            {doc.source !== 'generated' && <Pencil className="w-2 h-2 ml-1 inline" />}
                          </Badge>
                        )}
                        <span className="text-gray-400 text-[10px]">{new Date(doc.created_at).toLocaleDateString()}</span>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => viewDoc(doc)} data-testid={`view-doc-${doc.id}`}>
                          <Eye className="w-3.5 h-3.5 text-blue-500" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleDownloadDoc(doc)} data-testid={`dl-doc-${doc.id}`}>
                          <Download className="w-3.5 h-3.5 text-gray-500" />
                        </Button>
                        {doc.source !== 'generated' && (
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600" onClick={() => handleDeleteDoc(doc.id)} data-testid={`del-doc-${doc.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right column — Credit Notes */}
        <div className="lg:col-span-1">
          <Card className="border-gray-200 sticky top-4" data-testid="chat-notes-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <MessageSquare className="w-4 h-4 text-[#E31937]" />
                <h3 className="text-sm font-semibold text-gray-900 uppercase">Credit Notes</h3>
              </div>

              <div className="h-[400px] overflow-y-auto border border-gray-100 rounded-lg p-3 mb-3 bg-gray-50 space-y-2" data-testid="chat-messages">
                {chatNotes.length === 0 ? (
                  <p className="text-xs text-gray-400 italic text-center mt-16">No messages yet. Start the conversation.</p>
                ) : (
                  chatNotes.map(note => {
                    const isAdminMsg = note.role === 'admin';
                    return (
                      <div key={note.id} className={`flex flex-col ${isAdminMsg ? 'items-end' : 'items-start'}`} data-testid={`chat-msg-${note.id}`}>
                        <div className={`max-w-[85%] rounded-lg px-3 py-2 ${
                          isAdminMsg
                            ? 'bg-[#E31937] text-white'
                            : 'bg-white border border-gray-200 text-gray-800'
                        }`}>
                          <p className="text-sm whitespace-pre-wrap">{note.content}</p>
                        </div>
                        <p className="text-[10px] mt-0.5 px-1 text-gray-400">
                          {note.created_by} &middot; {new Date(note.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    );
                  })
                )}
                <div ref={chatEndRef} />
              </div>

              <div className="flex gap-2">
                <Textarea
                  placeholder="Type a note..."
                  value={chatMsg}
                  onChange={e => setChatMsg(e.target.value)}
                  className="bg-white border-gray-200 text-sm min-h-[40px] max-h-[80px] resize-none flex-1"
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendChat(); } }}
                  data-testid="chat-input"
                />
                <Button size="sm" className="bg-[#E31937] hover:bg-[#C4122C] text-white h-10 w-10 p-0 flex-shrink-0" onClick={handleSendChat} disabled={sendingChat || !chatMsg.trim()} data-testid="send-chat-btn">
                  {sendingChat ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Activity Log */}
          <Card className="border-gray-200 mt-5" data-testid="activity-log-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-900 uppercase flex items-center gap-1.5">
                  <Clock className="w-4 h-4 text-gray-500" /> Activity Log
                </h3>
                <span className="text-[10px] text-gray-400">{activityLog.length} events</span>
              </div>
              {activityLog.length === 0 ? (
                <p className="text-xs text-gray-400 italic text-center py-4">No activity recorded yet</p>
              ) : (
                <div className="max-h-[280px] overflow-y-auto space-y-0 pr-1" data-testid="activity-log-list">
                  {activityLog.map((act, i) => {
                    const icon = {
                      link_sent: <LinkIcon className="w-3 h-3 text-blue-500" />,
                      link_opened: <Eye className="w-3 h-3 text-amber-500" />,
                      client_upload: <Upload className="w-3 h-3 text-green-500" />,
                      staff_upload: <Upload className="w-3 h-3 text-blue-600" />,
                      stage_change: <RefreshCw className="w-3 h-3 text-purple-500" />,
                      note_added: <MessageSquare className="w-3 h-3 text-gray-500" />,
                      credit_submitted: <CreditCard className="w-3 h-3 text-purple-600" />,
                      credit_package_sent: <Send className="w-3 h-3 text-red-500" />,
                      deposit_sent: <DollarSign className="w-3 h-3 text-teal-500" />,
                      esign_sent: <PenTool className="w-3 h-3 text-purple-500" />,
                      esign_signed: <CheckCircle className="w-3 h-3 text-green-600" />,
                      doc_sign_sent: <Files className="w-3 h-3 text-purple-500" />,
                      doc_viewed: <Eye className="w-3 h-3 text-amber-500" />,
                      doc_signed: <CheckCircle className="w-3 h-3 text-green-600" />,
                      equipment_change: <Truck className="w-3 h-3 text-orange-500" />,
                      broker_notes_saved: <FileText className="w-3 h-3 text-indigo-500" />,
                    }[act.type] || <Clock className="w-3 h-3 text-gray-400" />;
                    const timeStr = act.created_at ? new Date(act.created_at).toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
                    return (
                      <div key={act.id || i} className="flex gap-2.5 py-2 border-b border-gray-50 last:border-0" data-testid={`activity-item-${i}`}>
                        <div className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center">
                          {icon}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[11px] text-gray-700 leading-snug">{act.message}</p>
                          <p className="text-[10px] text-gray-400 mt-0.5">{timeStr}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Approval Details Dialog (Admin Only) */}
      <Dialog open={approvalOpen} onOpenChange={setApprovalOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-md" data-testid="approval-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" /> Approval Details
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveApproval} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-500">Financing Amount ($)</label>
                <Input type="number" value={approvalForm.financing_amount || ''} onChange={e => setApprovalForm(f => ({ ...f, financing_amount: e.target.value }))} className="bg-gray-50 border-gray-200 mt-1" data-testid="approval-financing-amount" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Monthly Payment ($)</label>
                <Input type="number" value={approvalForm.monthly_payment || ''} onChange={e => setApprovalForm(f => ({ ...f, monthly_payment: e.target.value }))} className="bg-gray-50 border-gray-200 mt-1" data-testid="approval-monthly-payment" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Term (months)</label>
                <Input type="number" value={approvalForm.term || ''} onChange={e => setApprovalForm(f => ({ ...f, term: e.target.value }))} className="bg-gray-50 border-gray-200 mt-1" data-testid="approval-term" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Interest Rate (%)</label>
                <Input type="number" step="0.01" value={approvalForm.interest_rate || ''} onChange={e => setApprovalForm(f => ({ ...f, interest_rate: e.target.value }))} className="bg-gray-50 border-gray-200 mt-1" data-testid="approval-interest-rate" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Down Payment ($)</label>
                <Input type="number" value={approvalForm.downpayment || ''} onChange={e => setApprovalForm(f => ({ ...f, downpayment: e.target.value }))} className="bg-gray-50 border-gray-200 mt-1" data-testid="approval-downpayment" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Funding Date</label>
                <Input type="date" value={approvalForm.funding_date || ''} onChange={e => setApprovalForm(f => ({ ...f, funding_date: e.target.value }))} className="bg-gray-50 border-gray-200 mt-1" data-testid="approval-funding-date" />
              </div>
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button type="button" variant="outline" className="border-gray-300" onClick={() => setApprovalOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-green-600 hover:bg-green-700 text-white" disabled={savingApproval} data-testid="save-approval-btn">
                {savingApproval ? 'Saving...' : 'Approve & Save'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Send to Lender Dialog (Admin Only) */}
      <Dialog open={sendLenderOpen} onOpenChange={(open) => { setSendLenderOpen(open); if (!open) { setSendMode('lender'); setSelectedLenderId(''); setLenderEmail(''); } }}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-md" data-testid="send-lender-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-[#E31937]" /> Send Credit Package
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-100 text-sm">
              <p><span className="text-gray-500">Client:</span> <span className="font-medium">{deal?.client?.name || 'N/A'}</span></p>
              <p><span className="text-gray-500">Documents:</span> <span className="font-medium">{docs.length} file(s)</span></p>
              {brokerNotes && <p className="text-gray-500 mt-1 text-xs">Broker notes will be included</p>}
            </div>

            {/* Toggle: Saved Lender or Custom Email */}
            <div className="flex gap-1 p-1 bg-gray-100 rounded-lg">
              <button
                onClick={() => setSendMode('lender')}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-colors ${sendMode === 'lender' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                data-testid="mode-lender"
              >
                <Building2 className="w-3.5 h-3.5" /> Saved Lender
              </button>
              <button
                onClick={() => setSendMode('custom')}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-colors ${sendMode === 'custom' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                data-testid="mode-custom"
              >
                <Mail className="w-3.5 h-3.5" /> Custom Email
              </button>
            </div>

            {sendMode === 'lender' ? (
              <div className="space-y-2">
                {lenders.length === 0 ? (
                  <p className="text-xs text-gray-400 text-center py-3">No lenders found. Add lenders from the Lenders page.</p>
                ) : (
                  <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
                    {lenders.map(l => (
                      <label
                        key={l.id}
                        className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${selectedLenderId === l.id ? 'border-[#E31937] bg-red-50' : 'border-gray-200 hover:bg-gray-50'}`}
                        data-testid={`lender-option-${l.id}`}
                      >
                        <input
                          type="radio"
                          name="lender"
                          checked={selectedLenderId === l.id}
                          onChange={() => setSelectedLenderId(l.id)}
                          className="mt-0.5 accent-[#E31937]"
                        />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900">{l.name}</p>
                          {l.contact_name && <p className="text-xs text-gray-500">{l.contact_name}</p>}
                          <p className="text-xs text-gray-400">{l.contact_email || 'No email on file'}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div>
                <label className="text-xs text-gray-500 block mb-1">Recipient Email</label>
                <Input type="email" value={lenderEmail} onChange={e => setLenderEmail(e.target.value)} placeholder="lender@finance.com" className="bg-gray-50 border-gray-200" data-testid="lender-email-input" />
              </div>
            )}

            <Button
              className="w-full bg-[#E31937] hover:bg-[#C4122C] text-white"
              onClick={handleSendToLender}
              disabled={sendingPackage || (sendMode === 'lender' && !selectedLenderId) || (sendMode === 'custom' && !lenderEmail.trim())}
              data-testid="confirm-send-lender-btn"
            >
              {sendingPackage ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Sending...</> : 'Send Package'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* View All Units Modal */}
      <Dialog open={viewAllOpen} onOpenChange={setViewAllOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="view-all-units-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-[#E31937]" /> All Equipment ({deal?.units?.length || 0} units)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {(deal?.units || []).map((u, idx) => (
              <div key={u.id || idx} className="flex items-start gap-3 bg-gray-50 border border-gray-200 rounded-lg p-3" data-testid={`all-unit-${u.id || idx}`}>
                <div className="w-7 h-7 rounded-lg bg-[#E31937]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-[#E31937]">{idx + 1}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900">{u.year} {u.make} {u.model}</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 mt-1 text-xs text-gray-500">
                    <span>Stock #{u.stock_number || '—'}</span>
                    <span>VIN: {u.vin || '—'}</span>
                    <span>Mileage: {u.mileage_km ? `${Number(u.mileage_km).toLocaleString()} km` : '—'}</span>
                    <span className="text-[#E31937] font-semibold">{u.price ? `$${Number(u.price).toLocaleString()}` : '—'}</span>
                  </div>
                  <div className="flex gap-1.5 mt-2">
                    <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 border-gray-300 text-gray-600 hover:text-[#E31937]"
                      onClick={() => { setEditUnitData(u); setEditUnitMode('edit'); setPriceOverride(deal?.price_overrides?.[u.id]?.toString() || ''); setViewAllOpen(false); setEditUnitOpen(true); }}
                    ><Pencil className="w-2.5 h-2.5 mr-1" />Edit</Button>
                    <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 border-gray-300 text-gray-600 hover:text-blue-600"
                      onClick={() => { setEditUnitData(u); setEditUnitMode('swap'); setSwapSearch(''); setPriceOverride(''); openEquipPicker(); setViewAllOpen(false); setEditUnitOpen(true); }}
                    ><RefreshCw className="w-2.5 h-2.5 mr-1" />Swap</Button>
                    {(deal?.units?.length || 0) > 1 && (
                      <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 border-gray-300 text-red-400 hover:text-red-600"
                        onClick={() => handleRemoveUnit(u.id)} disabled={savingEquip}
                      ><Trash2 className="w-2.5 h-2.5 mr-1" />Delete</Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
            <Button size="sm" variant="outline" className="w-full text-xs border-dashed border-gray-300 text-gray-500 hover:text-[#E31937] hover:border-[#E31937]/30"
              onClick={() => { setViewAllOpen(false); openEquipPicker(); }} data-testid="add-from-view-all"
            ><Plus className="w-3 h-3 mr-1" /> Add Equipment</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Unit Modal */}
      <Dialog open={editUnitOpen} onOpenChange={setEditUnitOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-lg max-h-[80vh] overflow-y-auto" data-testid="edit-unit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editUnitMode === 'swap' ? <><RefreshCw className="w-5 h-5 text-blue-600" /> Swap Equipment</> : <><Pencil className="w-5 h-5 text-[#E31937]" /> Unit Details</>}
            </DialogTitle>
          </DialogHeader>
          {editUnitData && editUnitMode === 'edit' && (
            <div className="space-y-3">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-gray-500">Vehicle</span><span className="font-semibold text-gray-900">{editUnitData.year} {editUnitData.make} {editUnitData.model}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Stock #</span><span className="font-medium">{editUnitData.stock_number || '—'}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">VIN</span><span className="font-mono text-xs">{editUnitData.vin || '—'}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Mileage</span><span>{editUnitData.mileage_km ? `${Number(editUnitData.mileage_km).toLocaleString()} km` : '—'}</span></div>
                {editUnitData.engine && <div className="flex justify-between"><span className="text-gray-500">Engine</span><span>{editUnitData.engine}</span></div>}
                {editUnitData.horsepower && <div className="flex justify-between"><span className="text-gray-500">HP</span><span>{editUnitData.horsepower}</span></div>}
                {editUnitData.transmission && <div className="flex justify-between"><span className="text-gray-500">Transmission</span><span>{editUnitData.transmission}</span></div>}
                <div className="flex justify-between"><span className="text-gray-500">Listing Price</span><span className="font-semibold text-gray-400">{editUnitData.price ? `$${Number(editUnitData.price).toLocaleString()}` : '—'}</span></div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700">Deal Price ($)</label>
                <p className="text-[10px] text-gray-400 mb-1">Override the listing price for this deal only</p>
                <div className="flex gap-2">
                  <Input type="number" value={priceOverride} onChange={e => setPriceOverride(e.target.value)}
                    placeholder={editUnitData.price ? `${Number(editUnitData.price).toLocaleString()} (listing)` : 'Enter deal price'}
                    className="bg-gray-50 border-gray-200 text-sm flex-1" data-testid="edit-price-override" />
                  <Button size="sm" className="bg-[#E31937] hover:bg-[#C4122C] text-white text-xs px-4"
                    onClick={() => handleSaveEditPrice(editUnitData.id)} disabled={savingEquip}
                    data-testid="save-edit-price-btn"
                  >{savingEquip ? 'Saving...' : 'Save Price'}</Button>
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="flex-1 text-xs border-blue-300 text-blue-600 hover:bg-blue-50"
                  onClick={() => { setEditUnitMode('swap'); setSwapSearch(''); setPriceOverride(''); openEquipPicker(); }}
                  data-testid="switch-to-swap"
                ><RefreshCw className="w-3 h-3 mr-1" /> Swap with another unit</Button>
                {deal?.units?.length > 1 && (
                  <Button size="sm" variant="outline" className="text-xs border-red-300 text-red-500 hover:bg-red-50"
                    onClick={() => { handleRemoveUnit(editUnitData.id); setEditUnitOpen(false); }} disabled={savingEquip}
                    data-testid="delete-from-edit"
                  ><Trash2 className="w-3 h-3 mr-1" /> Delete</Button>
                )}
              </div>
            </div>
          )}
          {editUnitData && editUnitMode === 'swap' && (
            <div className="space-y-3">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-xs text-amber-700">
                Replacing: <span className="font-semibold">{editUnitData.year} {editUnitData.make} {editUnitData.model}</span>
              </div>
              <div>
                <label className="text-xs text-gray-500">Price Override (optional)</label>
                <Input type="number" value={priceOverride} onChange={e => setPriceOverride(e.target.value)} placeholder="Leave empty to use listing price" className="bg-gray-50 border-gray-200 mt-1 text-sm" data-testid="swap-price-override" />
              </div>
              <Input placeholder="Search inventory..." value={swapSearch} onChange={e => setSwapSearch(e.target.value)} className="bg-gray-50 border-gray-200" data-testid="swap-search" />
              <div className="max-h-[250px] overflow-y-auto space-y-1.5">
                {loadingInv ? <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div> :
                 filteredSwapInv.length === 0 ? <p className="text-sm text-gray-400 text-center py-4">No available units</p> :
                 filteredSwapInv.map(unit => (
                  <div key={unit.id} className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 px-3 py-2.5 rounded-lg cursor-pointer transition-colors border border-transparent hover:border-blue-200"
                    onClick={() => handleSwapUnit(editUnitData.id, unit)} data-testid={`swap-pick-${unit.id}`}
                  >
                    <div><p className="text-sm font-medium text-gray-900">{unit.year} {unit.make} {unit.model}</p><p className="text-xs text-gray-500">Stock #{unit.stock_number}</p></div>
                    <span className="text-sm font-semibold text-[#E31937]">{unit.price ? `$${Number(unit.price).toLocaleString()}` : '—'}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Equipment Picker */}
      <Dialog open={equipOpen} onOpenChange={setEquipOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-lg max-h-[80vh] overflow-y-auto" data-testid="equip-picker-dialog">
          <DialogHeader><DialogTitle>Select Equipment</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Button size="sm" variant="outline" className="w-full text-xs border-blue-300 text-blue-700" onClick={() => { setCustomEquip({ year: '', make: '', model: '', price: '', notes: '' }); setCustomEquipOpen(true); }} data-testid="equip-custom-btn">
              Custom / Private Sale
            </Button>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input placeholder="Search inventory..." value={invSearch} onChange={e => setInvSearch(e.target.value)} className="pl-9 bg-gray-50 border-gray-200" data-testid="equip-search" />
            </div>
            <div className="max-h-[300px] overflow-y-auto space-y-1.5">
              {loadingInv ? <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div> :
               filteredInv.length === 0 ? <p className="text-sm text-gray-400 text-center py-4">No available units</p> :
               filteredInv.map(unit => (
                <div key={unit.id} className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 px-3 py-2.5 rounded-lg cursor-pointer transition-colors border border-transparent hover:border-[#E31937]/20"
                  onClick={() => handleAddUnit(unit.id)} data-testid={`pick-inv-${unit.id}`}
                >
                  <div><p className="text-sm font-medium text-gray-900">{unit.year} {unit.make} {unit.model}</p><p className="text-xs text-gray-500">Stock #{unit.stock_number}</p></div>
                  <span className="text-sm font-semibold text-[#E31937]">{unit.price ? `$${Number(unit.price).toLocaleString()}` : '—'}</span>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Custom Equipment Dialog */}
      <Dialog open={customEquipOpen} onOpenChange={setCustomEquipOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-sm" data-testid="custom-equip-dialog">
          <DialogHeader><DialogTitle>Custom / Private Sale</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Year" value={customEquip.year} onChange={e => setCustomEquip(f => ({ ...f, year: e.target.value }))} className="bg-gray-50 border-gray-200" />
            <Input placeholder="Make" value={customEquip.make} onChange={e => setCustomEquip(f => ({ ...f, make: e.target.value }))} className="bg-gray-50 border-gray-200" />
            <Input placeholder="Model" value={customEquip.model} onChange={e => setCustomEquip(f => ({ ...f, model: e.target.value }))} className="bg-gray-50 border-gray-200" />
            <Input placeholder="Price" type="number" value={customEquip.price} onChange={e => setCustomEquip(f => ({ ...f, price: e.target.value }))} className="bg-gray-50 border-gray-200" />
            <Textarea placeholder="Notes (private sale details, condition, etc.)" value={customEquip.notes} onChange={e => setCustomEquip(f => ({ ...f, notes: e.target.value }))} className="bg-gray-50 border-gray-200" rows={2} />
            <Button className="w-full bg-[#E31937] text-white hover:bg-[#C4122C]" onClick={handleSaveCustomEquip} disabled={savingEquip} data-testid="save-custom-equip-btn">
              {savingEquip ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Document Lightbox */}
      <Dialog open={lightbox.open} onOpenChange={(open) => { if (!open) { if (lightbox.url) window.URL.revokeObjectURL(lightbox.url); setLightbox({ open: false, url: '', filename: '', type: '' }); } }}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-4xl max-h-[90vh] p-0" data-testid="doc-lightbox">
          <DialogHeader className="p-4 pb-2 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <DialogTitle className="text-sm font-medium truncate pr-4">{lightbox.filename}</DialogTitle>
              <Button size="sm" variant="outline" className="text-xs h-7 border-gray-300 flex-shrink-0" onClick={() => {
                const a = document.createElement('a');
                a.href = lightbox.url;
                a.download = lightbox.filename;
                a.click();
              }} data-testid="lightbox-download-btn">
                <Download className="w-3 h-3 mr-1" /> Download
              </Button>
            </div>
          </DialogHeader>
          <div className="p-4 flex items-center justify-center overflow-auto" style={{ maxHeight: 'calc(90vh - 80px)' }}>
            {lightbox.type === 'image' ? (
              <img src={lightbox.url} alt={lightbox.filename} className="max-w-full max-h-[70vh] object-contain rounded" data-testid="lightbox-image" />
            ) : lightbox.type === 'pdf' ? (
              <iframe src={lightbox.url} title={lightbox.filename} className="w-full h-[70vh] border-0 rounded" data-testid="lightbox-pdf" />
            ) : (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500">Preview not available for this file type</p>
                <p className="text-xs text-gray-400 mt-1">{lightbox.filename}</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Document Category Upload Dialog */}
      <Dialog open={docUploadOpen} onOpenChange={setDocUploadOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-md max-h-[80vh] overflow-y-auto" data-testid="doc-category-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-[#E31937]" /> Categorize Documents
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-gray-500">Select a document type for each file before uploading.</p>
            {pendingFiles.map((entry, i) => (
              <div key={i} className="bg-gray-50 rounded-lg p-3 border border-gray-200" data-testid={`pending-file-${i}`}>
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                  <span className="text-sm text-gray-800 truncate flex-1">{entry.file.name}</span>
                  <span className="text-[10px] text-gray-400">{(entry.file.size / 1024).toFixed(0)} KB</span>
                </div>
                <Select value={entry.category} onValueChange={v => setPendingFiles(prev => prev.map((e, idx) => idx === i ? { ...e, category: v, customCategory: v === 'Other' ? e.customCategory : '' } : e))}>
                  <SelectTrigger className="h-8 text-xs bg-white border-gray-200" data-testid={`doc-cat-select-${i}`}>
                    <SelectValue placeholder="Select document type..." />
                  </SelectTrigger>
                  <SelectContent>
                    {DOC_CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat} className="text-xs">{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {entry.category === 'Other' && (
                  <Input
                    placeholder="Enter category name..."
                    value={entry.customCategory}
                    onChange={e => setPendingFiles(prev => prev.map((en, idx) => idx === i ? { ...en, customCategory: e.target.value } : en))}
                    className="mt-2 h-8 text-xs bg-white border-gray-200"
                    data-testid={`doc-cat-custom-${i}`}
                  />
                )}
              </div>
            ))}
            <Button className="w-full bg-[#E31937] hover:bg-[#C4122C] text-white text-sm" onClick={handleConfirmUpload} disabled={uploading} data-testid="confirm-upload-btn">
              {uploading ? 'Uploading...' : `Upload ${pendingFiles.length} File(s)`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <LenderPackagePreview open={lenderPkgPreviewOpen} onClose={() => setLenderPkgPreviewOpen(false)} dealId={dealId} />
    </div>
  );
}
